/** /platform-notices — ops' word on a problem on our side (client MCP plan §5.2 "Was it you?",
 *  §7.1; WPS FE-03, backend P1-08).
 *
 *  The triage check `platform_notice` quotes a notice to a client's AI when the notice applies to
 *  its tenant and overlaps its bookings' time with us: the capability, the window and the wording
 *  only — never the audience, the tenant list, the internal note or the author. One row per
 *  notice, the latest start first; a row opens it for editing (`?edit=<id>` deep-links it, `?new`
 *  opens a new one).
 *
 *  Filters live in the URL (linkable, preset-covered): tenant (the notices that apply to those
 *  tenants: every tenant's and those listing one of them; console scope aware), capability,
 *  audience and whether a notice is active now. Everything needs
 *  `console_platform_notices_manage` (Ops Admin by default). */
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { getGridSingleSelectOperators, type GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import * as noticesApi from '@/api/platformNotices';
import { platformNoticeKeys, type PlatformNotice } from '@/api/platformNotices';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { operatorActions } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { useEditIntent } from '@/hooks/useEditIntent';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { PlatformNoticeFormDialog } from './PlatformNoticeFormDialog';
import { tenantGrid } from './platformNoticeLinks';

/** Capability keys are lower-case words; the backend refuses an unknown one, so a hand-edited
 *  URL keeps only the well-formed. */
const keyList = (v: string): string | undefined => {
  const keys = v.split(',').filter((x) => /^[a-z_]+$/.test(x));
  return keys.length ? keys.join(',') : undefined;
};

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'capability', toApi: keyList, column: { field: 'capability', operator: 'is' } },
  {
    param: 'audience',
    toApi: (v) => (v === 'all' || v === 'listed' ? v : undefined),
    column: { field: 'audience', operator: 'is' },
  },
  {
    param: 'active',
    toApi: (v) => (v === 'true' ? true : v === 'false' ? false : undefined),
    column: { field: 'active', operator: 'is' },
  },
];
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

/** Column order (mirrors the columns memo — keep in sync). */
const FIELD_ORDER = [
  'active',
  'starts_at',
  'ends_at',
  'capability',
  'wording',
  'audience',
  'internal_note',
  'created_by',
  'updated_ts',
  'created_ts',
  'id',
];
const DEFAULT_HIDDEN = ['created_ts', 'id'];

const isOperator = getGridSingleSelectOperators().filter((op) => op.value === 'is');

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'Every tenant' },
  { value: 'listed', label: 'Tenants listed' },
];
const ACTIVE_OPTIONS = [
  { value: 'true', label: 'Active now' },
  { value: 'false', label: 'Not active' },
];

function When({ iso, empty }: { iso: string | null; empty: string }) {
  if (!iso) {
    return (
      <Typography variant="body2" component="span" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  return (
    <Tooltip title={formatRelativeTime(iso)} describeChild>
      <span>{formatDate(iso)}</span>
    </Tooltip>
  );
}

/** "Every tenant", or the tenants listed (the first few, then a count). */
function AudienceCell({ notice }: { notice: PlatformNotice }) {
  if (notice.audience === 'all') return <>Every tenant</>;
  const shown = notice.tenants.slice(0, 3);
  const more = notice.tenants.length - shown.length;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
      {shown.map((t) => (
        <ValueActions key={t.id} actions={operatorActions(t, tenantGrid(t.id))}>
          {t.code || t.name}
        </ValueActions>
      ))}
      {more > 0 && (
        <Typography variant="body2" component="span" color="text.secondary">
          +{more}
        </Typography>
      )}
    </Stack>
  );
}

export function PlatformNoticesPage() {
  // null = closed; 'new' = create; an id = edit.
  const [dialog, setDialog] = useState<number | 'new' | null>(null);
  useCreateIntent(() => setDialog('new'));
  useEditIntent((id) => setDialog(id));

  const presets = useGridPresets({
    grid: 'platform_notices',
    defaultOrder: FIELD_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-starts_at',
    filters: PRESET_FILTERS,
  });
  const {
    filterModel,
    onFilterModelChange,
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const capabilities = (getParam('capability') ?? '').split(',').filter(Boolean);
  const audience = getParam('audience') ?? '';
  const active = getParam('active') ?? '';

  const capabilityQuery = useQuery({
    queryKey: platformNoticeKeys.capabilities,
    queryFn: noticesApi.capabilities,
    staleTime: 60 * 60_000,
  });
  const capabilityOptions = useMemo(
    () => (capabilityQuery.data?.capabilities ?? []).map((c) => ({ value: c.key, label: c.label })),
    [capabilityQuery.data],
  );
  const labelOf = (key: string) => capabilityOptions.find((o) => o.value === key)?.label ?? key;

  const columns = useMemo<GridColDef<PlatformNotice>[]>(
    () => [
      {
        field: 'active',
        headerName: 'Now',
        width: 110,
        sortable: false,
        type: 'singleSelect',
        valueOptions: ACTIVE_OPTIONS,
        filterOperators: isOperator,
        valueGetter: (_, row) => String(row.active),
        renderCell: (p) =>
          p.row.active ? (
            <Chip size="small" color="warning" label="Active" />
          ) : (
            <Typography variant="body2" component="span" color="text.secondary">
              {new Date(p.row.starts_at).getTime() > Date.now() ? 'Upcoming' : 'Ended'}
            </Typography>
          ),
      },
      {
        field: 'starts_at',
        headerName: 'Started',
        width: 170,
        filterable: false,
        renderCell: (p) => <When iso={p.row.starts_at} empty="—" />,
      },
      {
        field: 'ends_at',
        headerName: 'Ended',
        width: 170,
        filterable: false,
        renderCell: (p) => <When iso={p.row.ends_at} empty="Still open" />,
      },
      {
        field: 'capability',
        headerName: 'What had the problem',
        width: 220,
        type: 'singleSelect',
        valueOptions: capabilityOptions,
        filterOperators: isOperator,
        renderCell: (p) => p.row.capability_label || p.row.capability,
      },
      {
        field: 'wording',
        headerName: 'Client wording',
        flex: 1,
        minWidth: 260,
        sortable: false,
        filterable: false,
      },
      {
        field: 'audience',
        headerName: 'For',
        width: 200,
        sortable: false,
        type: 'singleSelect',
        valueOptions: AUDIENCE_OPTIONS,
        filterOperators: isOperator,
        renderCell: (p) => <AudienceCell notice={p.row} />,
      },
      {
        field: 'internal_note',
        headerName: 'Internal note',
        width: 220,
        sortable: false,
        filterable: false,
      },
      {
        field: 'created_by',
        headerName: 'Posted by',
        width: 200,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => row.created_by?.email ?? '',
      },
      {
        field: 'updated_ts',
        headerName: 'Saved',
        width: 140,
        filterable: false,
        renderCell: (p) => formatRelativeTime(p.row.updated_ts),
      },
      {
        field: 'created_ts',
        headerName: 'Posted',
        width: 140,
        filterable: false,
        renderCell: (p) => formatRelativeTime(p.row.created_ts),
      },
      copyableColumn({ field: 'id', headerName: 'ID', width: 80, filterable: false }),
    ],
    [capabilityOptions],
  );

  const queryFn = (params: ResourceDataGridParams) =>
    noticesApi.list({
      ...(params.extra as noticesApi.PlatformNoticeFilters),
      ...(params.search ? { search: params.search } : {}),
      ordering: params.ordering ?? presets.ordering,
      page: params.page,
      page_size: params.pageSize,
    });

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 4,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 260 }}>
          <TenantPicker
            multiple
            label="Applies to"
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'active',
      priority: 3,
      active: active !== '',
      node: (
        <TextField
          select
          size="small"
          label="Now"
          value={active}
          onChange={(e) => setParam('active', e.target.value || null)}
          sx={{ width: 150 }}
        >
          <MenuItem value="">Any</MenuItem>
          {ACTIVE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'capability',
      priority: 2,
      active: capabilities.length > 0,
      node: (
        <TextField
          select
          size="small"
          label="What had the problem"
          value={capabilities}
          onChange={(e) =>
            setParam('capability', (e.target.value as unknown as string[]).join(',') || null)
          }
          sx={{ width: 220 }}
          SelectProps={{
            multiple: true,
            renderValue: (sel) => (sel as string[]).map(labelOf).join(', '),
          }}
        >
          {capabilityOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              <Checkbox size="small" checked={capabilities.includes(o.value)} sx={{ py: 0 }} />
              <ListItemText primary={o.label} />
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'audience',
      priority: 1,
      active: audience !== '',
      node: (
        <TextField
          select
          size="small"
          label="For"
          value={audience}
          onChange={(e) => setParam('audience', e.target.value || null)}
          sx={{ width: 160 }}
        >
          <MenuItem value="">Anyone</MenuItem>
          {AUDIENCE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
  ];

  const newButton = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog('new')}>
      New notice
    </Button>
  );

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="platform-notices"
        title="Platform notices"
        subtitle="Our word on a problem on our side, quoted to clients’ AI when it explains their bookings."
        actions={newButton}
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<PlatformNotice>
          queryKey={platformNoticeKeys.list}
          queryFn={queryFn}
          resetPageOnQueryChange
          columns={orderColumns(columns, presets.orderedFields)}
          defaultSort={{ field: 'starts_at', sort: 'desc' }}
          extra={extra}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          onRowClick={(id) => setDialog(id)}
          emptyMessage="No platform notices yet. Post one when something on our side goes wrong, so clients’ AI can tell them."
          emptyAction={newButton}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Wording or internal note',
            exportFileName: 'platform-notices',
            presets: presets.toolbar,
          }}
        />
      )}

      {dialog !== null && (
        <PlatformNoticeFormDialog
          noticeId={dialog === 'new' ? null : dialog}
          pageTenants={pageTenants}
          onClose={() => setDialog(null)}
        />
      )}
    </Box>
  );
}
